import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { useRealTime } from '../context/RealTimeContext';
import toast from 'react-hot-toast';

// Chart components for visualizations
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut, Pie } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
);

const SalesTargets = () => {
  const { subscribe, unsubscribe } = useRealTime();
  const [targets, setTargets] = useState([]);
  const [salesExecutives, setSalesExecutives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTarget, setEditingTarget] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [filters, setFilters] = useState({
    salesExecutiveId: '',
    period: '',
    status: ''
  });

  // Enhanced dashboard state
  const [viewMode, setViewMode] = useState('summary'); // summary, monthly, individual, collective, incentive, executive, salesman
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedExecutive, setSelectedExecutive] = useState('');
  const [showDashboard, setShowDashboard] = useState(false);
  const [performanceData, setPerformanceData] = useState(null);
  const [targetProgress, setTargetProgress] = useState({});

  // Incentive calculation state
  const [showIncentiveModal, setShowIncentiveModal] = useState(false);
  const [incentiveConfig, setIncentiveConfig] = useState({
    baseCommission: 2.5, // Base commission percentage
    targetAchievementBonus: 5, // Bonus for achieving target
    overAchievementBonus: 7.5, // Bonus for over-achievement
    newCustomerBonus: 500, // Fixed bonus per new customer
    orderVolumeBonus: 100, // Fixed bonus per order
    performanceTiers: [
      { min: 0, max: 80, multiplier: 0.5, name: 'Below Target' },
      { min: 80, max: 100, multiplier: 1.0, name: 'Target Achieved' },
      { min: 100, max: 120, multiplier: 1.25, name: 'Over Achievement' },
      { min: 120, max: 150, multiplier: 1.5, name: 'High Performance' },
      { min: 150, max: 999, multiplier: 2.0, name: 'Excellence' }
    ]
  });

  // Form state for creating/editing targets
  const [formData, setFormData] = useState({
    salesExecutiveId: '',
    period: 'monthly',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    quarter: Math.ceil((new Date().getMonth() + 1) / 3),
    startDate: '',
    endDate: '',
    newCustomerTarget: 0,
    revenueTarget: 0,
    orderTarget: 0,
    notes: ''
  });

  const fetchTargets = useCallback(async (showNotification = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.salesExecutiveId) params.append('salesExecutiveId', filters.salesExecutiveId);
      if (filters.period) params.append('period', filters.period);
      if (filters.status) params.append('status', filters.status);

      const data = await apiService.get(`/sales-targets?${params}`);

      // Use functional update to avoid dependency on targets
      setTargets(prevTargets => {
        // Check if there are changes (compare with previous targets)
        const hasChanges = JSON.stringify(data) !== JSON.stringify(prevTargets);

        // Show notification if there are changes and notification is requested
        if (showNotification && hasChanges && prevTargets.length > 0) {
          toast.success('Sales targets updated with new data!');
        }

        return data;
      });

      setError(''); // Clear any previous errors
      setLastRefresh(new Date()); // Update last refresh time
    } catch (err) {
      setError('Failed to fetch sales targets');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchSalesExecutives = useCallback(async () => {
    try {
      const data = await apiService.get('/users?role=sales_executive');
      setSalesExecutives(data);
    } catch (err) {
      console.error('Failed to fetch sales executives:', err);
    }
  }, []);

  useEffect(() => {
    fetchTargets();
    fetchSalesExecutives();
  }, [fetchTargets, fetchSalesExecutives]);

  // Initialize form with current date range
  useEffect(() => {
    const initialFormData = {
      salesExecutiveId: '',
      period: 'monthly',
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      quarter: Math.ceil((new Date().getMonth() + 1) / 3),
      startDate: '',
      endDate: '',
      newCustomerTarget: 0,
      revenueTarget: 0,
      orderTarget: 0,
      notes: ''
    };
    setFormData(updateDateRange(initialFormData));
  }, []);

  // Subscribe to real-time updates
  useEffect(() => {
    const handleOrderUpdate = (data) => {
      console.log('🔄 Sales Targets received order update:', data);
      toast.success('New order activity detected! Refreshing sales targets...');

      // Refresh targets when order is approved or invoice is created
      fetchTargets(true);
    };

    // Subscribe to order and invoice events
    subscribe('orderApproved', handleOrderUpdate, 'SalesTargets');
    subscribe('invoiceCreated', handleOrderUpdate, 'SalesTargets');
    subscribe('deliveryOrderCreated', handleOrderUpdate, 'SalesTargets');

    // Cleanup subscriptions
    return () => {
      unsubscribe('orderApproved', 'SalesTargets');
      unsubscribe('invoiceCreated', 'SalesTargets');
      unsubscribe('deliveryOrderCreated', 'SalesTargets');
    };
  }, [subscribe, unsubscribe]); // Removed fetchTargets from dependencies

  // Auto-refresh sales targets every 30 seconds to catch new orders
  useEffect(() => {
    const interval = setInterval(() => {
      fetchTargets(true); // Show notification if changes detected
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, []); // Removed fetchTargets dependency

  // Refresh data when user returns to the tab (focus event)
  useEffect(() => {
    const handleFocus = () => {
      fetchTargets(true); // Show notification if changes detected
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []); // Removed fetchTargets dependency

  // Calculate date range based on period, year, month, and quarter
  const calculateDateRange = (period, year, month = null, quarter = null) => {
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    switch (period) {
      case 'monthly':
        const startOfMonth = new Date(year, month - 1, 1);
        const endOfMonth = new Date(year, month, 0);
        return {
          startDate: formatDate(startOfMonth),
          endDate: formatDate(endOfMonth)
        };

      case 'quarterly':
        const quarterStart = new Date(year, (quarter - 1) * 3, 1);
        const quarterEnd = new Date(year, quarter * 3, 0);
        return {
          startDate: formatDate(quarterStart),
          endDate: formatDate(quarterEnd)
        };

      case 'yearly':
        const yearStart = new Date(year, 0, 1);
        const yearEnd = new Date(year, 11, 31);
        return {
          startDate: formatDate(yearStart),
          endDate: formatDate(yearEnd)
        };

      default:
        return { startDate: '', endDate: '' };
    }
  };

  // Update dates when period, year, month, or quarter changes
  const updateDateRange = (newFormData) => {
    const { period, year, month, quarter } = newFormData;
    const { startDate, endDate } = calculateDateRange(period, year, month, quarter);
    return { ...newFormData, startDate, endDate };
  };

  // Intelligent allocation of monthly targets from annual targets
  const allocateMonthlyTargets = async (annualTarget) => {
    const { salesExecutiveId, year, newCustomerTarget, revenueTarget, orderTarget, notes } = annualTarget;

    // Business intelligence factors for monthly allocation (sums to 100%)
    const monthlyFactors = {
      1: 0.07,   // January - 7% (post-holiday recovery)
      2: 0.08,   // February - 8% (gradual pickup)
      3: 0.09,   // March - 9% (end of financial year)
      4: 0.08,   // April - 8% (new financial year start)
      5: 0.09,   // May - 9% (steady growth)
      6: 0.10,   // June - 10% (mid-year peak)
      7: 0.08,   // July - 8% (monsoon impact)
      8: 0.08,   // August - 8% (monsoon continues)
      9: 0.09,   // September - 9% (festival preparation)
      10: 0.10,  // October - 10% (festival season)
      11: 0.09,  // November - 9% (festival aftermath)
      12: 0.05   // December - 5% (year-end push) - adjusted to sum to 100%
    };

    const monthlyTargets = [];

    for (let month = 1; month <= 12; month++) {
      const factor = monthlyFactors[month];
      const { startDate, endDate } = calculateDateRange('monthly', year, month);

      monthlyTargets.push({
        salesExecutiveId: parseInt(salesExecutiveId),
        period: 'monthly',
        year: year,
        month: month,
        quarter: Math.ceil(month / 3),
        startDate: startDate,
        endDate: endDate,
        newCustomerTarget: Math.round(newCustomerTarget * factor),
        revenueTarget: Math.round(revenueTarget * factor),
        orderTarget: Math.round(orderTarget * factor),
        notes: `Auto-allocated from annual target (${Math.round(factor * 100)}% of annual)`
      });
    }

    return monthlyTargets;
  };

  // Create annual target and auto-allocate monthly targets
  const handleCreateAnnualTarget = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // First create the annual target
      const annualResponse = await apiService.post('/sales-targets', formData);

      // Then create monthly targets
      const monthlyTargets = await allocateMonthlyTargets(formData);

      // Create all monthly targets
      const monthlyPromises = monthlyTargets.map(target =>
        apiService.post('/sales-targets', target)
      );

      await Promise.all(monthlyPromises);

      setShowCreateModal(false);
      setFormData({
        salesExecutiveId: '',
        period: 'monthly',
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        quarter: Math.ceil((new Date().getMonth() + 1) / 3),
        startDate: '',
        endDate: '',
        newCustomerTarget: 0,
        revenueTarget: 0,
        orderTarget: 0,
        notes: ''
      });

      toast.success(`Annual target created with ${monthlyTargets.length} monthly targets auto-allocated!`);
      fetchTargets();
    } catch (err) {
      setError('Failed to create annual target with monthly allocation');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Intelligent summary calculations
  const calculateSummaryMetrics = () => {
    const currentYear = selectedYear;
    const currentMonth = new Date().getMonth() + 1;

    const yearTargets = targets.filter(t =>
      t.year === currentYear &&
      (!selectedExecutive || t.salesExecutiveId.toString() === selectedExecutive)
    );

    const monthlyTargets = yearTargets.filter(t => t.period === 'monthly');
    const completedMonths = monthlyTargets.filter(t => t.month <= currentMonth);

    // Calculate totals
    const totalRevenueTarget = yearTargets
      .filter(t => t.period === 'yearly')
      .reduce((sum, t) => sum + t.revenueTarget, 0);

    const totalCustomerTarget = yearTargets
      .filter(t => t.period === 'yearly')
      .reduce((sum, t) => sum + t.newCustomerTarget, 0);

    const totalOrderTarget = yearTargets
      .filter(t => t.period === 'yearly')
      .reduce((sum, t) => sum + t.orderTarget, 0);

    // Calculate progress (assuming we have actual performance data)
    const revenueProgress = 0; // This would come from actual sales data
    const customerProgress = 0;
    const orderProgress = 0;

    return {
      totalRevenueTarget,
      totalCustomerTarget,
      totalOrderTarget,
      revenueProgress,
      customerProgress,
      orderProgress,
      completedMonths: completedMonths.length,
      totalMonths: 12,
      progressPercentage: Math.round((completedMonths.length / 12) * 100)
    };
  };

  // Generate monthly breakdown data
  const generateMonthlyBreakdown = () => {
    const currentYear = selectedYear;
    const monthlyTargets = targets.filter(t =>
      t.year === currentYear &&
      t.period === 'monthly' &&
      (!selectedExecutive || t.salesExecutiveId.toString() === selectedExecutive)
    );

    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    const monthlyData = monthNames.map((month, index) => {
      const monthTarget = monthlyTargets.find(t => t.month === index + 1);
      return {
        month,
        revenueTarget: monthTarget?.revenueTarget || 0,
        customerTarget: monthTarget?.newCustomerTarget || 0,
        orderTarget: monthTarget?.orderTarget || 0,
        revenueActual: 0, // Would come from actual sales data
        customerActual: 0,
        orderActual: 0
      };
    });

    return monthlyData;
  };

  // Generate executive performance comparison
  const generateExecutiveComparison = () => {
    const currentYear = selectedYear;
    const executiveTargets = targets.filter(t =>
      t.year === currentYear &&
      t.period === 'yearly'
    );

    return executiveTargets.map(target => {
      const executive = salesExecutives.find(se => se.id === target.salesExecutiveId);
      return {
        name: executive?.name || 'Unknown',
        revenueTarget: target.revenueTarget,
        customerTarget: target.newCustomerTarget,
        orderTarget: target.orderTarget,
        revenueActual: 0, // Would come from actual sales data
        customerActual: 0,
        orderActual: 0
      };
    });
  };

  // Chart configurations
  const getMonthlyChartData = () => {
    const monthlyData = generateMonthlyBreakdown();

    return {
      labels: monthlyData.map(d => d.month),
      datasets: [
        {
          label: 'Revenue Target',
          data: monthlyData.map(d => d.revenueTarget),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4,
          fill: true
        },
        {
          label: 'Revenue Actual',
          data: monthlyData.map(d => d.revenueActual),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          tension: 0.4,
          fill: true
        }
      ]
    };
  };

  const getTeamExecutiveChartData = () => {
    const executiveData = generateExecutiveComparison();

    return {
      labels: executiveData.map(d => d.name),
      datasets: [
        {
          label: 'Revenue Target',
          data: executiveData.map(d => d.revenueTarget),
          backgroundColor: 'rgba(59, 130, 246, 0.8)',
        },
        {
          label: 'Revenue Actual',
          data: executiveData.map(d => d.revenueActual),
          backgroundColor: 'rgba(34, 197, 94, 0.8)',
        }
      ]
    };
  };

  const getProgressChartData = () => {
    const summary = calculateSummaryMetrics();

    return {
      labels: ['Revenue', 'Customers', 'Orders'],
      datasets: [{
        data: [
          summary.revenueProgress,
          summary.customerProgress,
          summary.orderProgress
        ],
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(34, 197, 94, 0.8)',
          'rgba(251, 146, 60, 0.8)'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }]
    };
  };

  // Incentive Calculation Functions
  const calculateIncentive = (executiveId, period = 'monthly', year = selectedYear) => {
    const executive = salesExecutives.find(se => se.id === executiveId);
    if (!executive) return null;

    // Get target and actual performance data
    const target = targets.find(t =>
      t.salesExecutiveId === executiveId &&
      t.period === period &&
      t.year === year
    );

    if (!target) return null;

    // Mock actual performance data (in real implementation, this would come from sales data)
    const actualPerformance = {
      revenue: target.revenueTarget * 0.85, // 85% of target for demo
      customers: target.newCustomerTarget * 0.9, // 90% of target
      orders: target.orderTarget * 0.88 // 88% of target
    };

    // Calculate performance percentages
    const revenueAchievement = (actualPerformance.revenue / target.revenueTarget) * 100;
    const customerAchievement = (actualPerformance.customers / target.newCustomerTarget) * 100;
    const orderAchievement = (actualPerformance.orders / target.orderTarget) * 100;

    // Determine performance tier
    const tier = incentiveConfig.performanceTiers.find(t =>
      revenueAchievement >= t.min && revenueAchievement < t.max
    ) || incentiveConfig.performanceTiers[0];

    // Calculate base commission
    const baseCommission = (actualPerformance.revenue * incentiveConfig.baseCommission) / 100;

    // Calculate performance multiplier
    const performanceMultiplier = tier.multiplier;

    // Calculate bonuses
    const targetAchievementBonus = revenueAchievement >= 100 ?
      (target.revenueTarget * incentiveConfig.targetAchievementBonus) / 100 : 0;

    const overAchievementBonus = revenueAchievement > 120 ?
      (actualPerformance.revenue - target.revenueTarget) * (incentiveConfig.overAchievementBonus / 100) : 0;

    const newCustomerBonus = actualPerformance.customers * incentiveConfig.newCustomerBonus;
    const orderVolumeBonus = actualPerformance.orders * incentiveConfig.orderVolumeBonus;

    // Calculate total incentive
    const totalIncentive = (baseCommission * performanceMultiplier) +
      targetAchievementBonus +
      overAchievementBonus +
      newCustomerBonus +
      orderVolumeBonus;

    return {
      executive: executive.name,
      period: period,
      year: year,
      target: {
        revenue: target.revenueTarget,
        customers: target.newCustomerTarget,
        orders: target.orderTarget
      },
      actual: actualPerformance,
      achievement: {
        revenue: revenueAchievement,
        customers: customerAchievement,
        orders: orderAchievement
      },
      tier: tier,
      breakdown: {
        baseCommission: baseCommission,
        performanceMultiplier: performanceMultiplier,
        adjustedCommission: baseCommission * performanceMultiplier,
        targetAchievementBonus: targetAchievementBonus,
        overAchievementBonus: overAchievementBonus,
        newCustomerBonus: newCustomerBonus,
        orderVolumeBonus: orderVolumeBonus
      },
      totalIncentive: totalIncentive
    };
  };

  const calculateTeamIncentives = () => {
    const teamIncentives = salesExecutives.map(exec => {
      const incentive = calculateIncentive(exec.id);
      return incentive;
    }).filter(Boolean);

    const totalTeamIncentive = teamIncentives.reduce((sum, inc) => sum + inc.totalIncentive, 0);
    const avgIncentive = totalTeamIncentive / teamIncentives.length;

    return {
      individual: teamIncentives,
      summary: {
        totalIncentive: totalTeamIncentive,
        averageIncentive: avgIncentive,
        highestIncentive: Math.max(...teamIncentives.map(inc => inc.totalIncentive)),
        lowestIncentive: Math.min(...teamIncentives.map(inc => inc.totalIncentive))
      }
    };
  };

  const getIncentiveChartData = () => {
    const teamIncentives = calculateTeamIncentives();

    return {
      labels: teamIncentives.individual.map(inc => inc.executive),
      datasets: [{
        label: 'Total Incentive (₹)',
        data: teamIncentives.individual.map(inc => inc.totalIncentive),
        backgroundColor: teamIncentives.individual.map(inc => {
          if (inc.achievement.revenue >= 120) return 'rgba(34, 197, 94, 0.8)'; // Green for high performance
          if (inc.achievement.revenue >= 100) return 'rgba(59, 130, 246, 0.8)'; // Blue for target achieved
          if (inc.achievement.revenue >= 80) return 'rgba(251, 146, 60, 0.8)'; // Orange for near target
          return 'rgba(239, 68, 68, 0.8)'; // Red for below target
        }),
        borderWidth: 2,
        borderColor: '#fff'
      }]
    };
  };

  const getPerformanceTierChartData = () => {
    const teamIncentives = calculateTeamIncentives();
    const tierCounts = {};

    teamIncentives.individual.forEach(inc => {
      const tierName = inc.tier.name;
      tierCounts[tierName] = (tierCounts[tierName] || 0) + 1;
    });

    return {
      labels: Object.keys(tierCounts),
      datasets: [{
        data: Object.values(tierCounts),
        backgroundColor: [
          'rgba(239, 68, 68, 0.8)',   // Red for Below Target
          'rgba(59, 130, 246, 0.8)',  // Blue for Target Achieved
          'rgba(251, 146, 60, 0.8)',  // Orange for Over Achievement
          'rgba(34, 197, 94, 0.8)',   // Green for High Performance
          'rgba(168, 85, 247, 0.8)'   // Purple for Excellence
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }]
    };
  };

  // Executive Dashboard Functions
  const getExecutivePerformanceData = (executiveId) => {
    const executive = salesExecutives.find(se => se.id === executiveId);
    if (!executive) return null;

    // Get all targets for this executive
    const executiveTargets = targets.filter(t => t.salesExecutiveId === executiveId);

    // Mock actual performance data (in real implementation, this would come from sales data)
    const performanceData = executiveTargets.map(target => {
      const actualRevenue = target.revenueTarget * (0.7 + Math.random() * 0.6); // 70% to 130% of target
      const actualCustomers = target.newCustomerTarget * (0.6 + Math.random() * 0.8); // 60% to 140% of target
      const actualOrders = target.orderTarget * (0.65 + Math.random() * 0.7); // 65% to 135% of target

      const revenueAchievement = (actualRevenue / target.revenueTarget) * 100;
      const customerAchievement = (actualCustomers / target.newCustomerTarget) * 100;
      const orderAchievement = (actualOrders / target.orderTarget) * 100;

      const revenueShortfall = Math.max(0, target.revenueTarget - actualRevenue);
      const customerShortfall = Math.max(0, target.newCustomerTarget - actualCustomers);
      const orderShortfall = Math.max(0, target.orderTarget - actualOrders);

      const incentive = calculateIncentive(executiveId, target.period, target.year);

      return {
        period: target.period,
        year: target.year,
        month: target.month,
        quarter: target.quarter,
        target: {
          revenue: target.revenueTarget,
          customers: target.newCustomerTarget,
          orders: target.orderTarget
        },
        actual: {
          revenue: actualRevenue,
          customers: actualCustomers,
          orders: actualOrders
        },
        achievement: {
          revenue: revenueAchievement,
          customers: customerAchievement,
          orders: orderAchievement
        },
        shortfall: {
          revenue: revenueShortfall,
          customers: customerShortfall,
          orders: orderShortfall
        },
        incentive: incentive?.totalIncentive || 0,
        tier: incentive?.tier?.name || 'Below Target'
      };
    });

    return {
      executive: executive,
      performance: performanceData,
      summary: {
        totalTargetRevenue: performanceData.reduce((sum, p) => sum + p.target.revenue, 0),
        totalActualRevenue: performanceData.reduce((sum, p) => sum + p.actual.revenue, 0),
        totalTargetCustomers: performanceData.reduce((sum, p) => sum + p.target.customers, 0),
        totalActualCustomers: performanceData.reduce((sum, p) => sum + p.actual.customers, 0),
        totalTargetOrders: performanceData.reduce((sum, p) => sum + p.target.orders, 0),
        totalActualOrders: performanceData.reduce((sum, p) => sum + p.actual.orders, 0),
        totalIncentive: performanceData.reduce((sum, p) => sum + p.incentive, 0),
        totalShortfall: {
          revenue: performanceData.reduce((sum, p) => sum + p.shortfall.revenue, 0),
          customers: performanceData.reduce((sum, p) => sum + p.shortfall.customers, 0),
          orders: performanceData.reduce((sum, p) => sum + p.shortfall.orders, 0)
        }
      }
    };
  };

  const getIndividualExecutiveChartData = (executiveId) => {
    const data = getExecutivePerformanceData(executiveId);
    if (!data || !data.performance || !Array.isArray(data.performance)) return null;

    const labels = data.performance.map(p => {
      if (p.period === 'monthly' && p.month) return `${p.year}-${p.month.toString().padStart(2, '0')}`;
      if (p.period === 'quarterly' && p.quarter) return `Q${p.quarter} ${p.year}`;
      return `${p.year}`;
    });

    return {
      labels: labels,
      datasets: [
        {
          label: 'Target Revenue',
          data: data.performance.map(p => p.target.revenue),
          borderColor: 'rgba(59, 130, 246, 1)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderWidth: 2,
          fill: false
        },
        {
          label: 'Actual Revenue',
          data: data.performance.map(p => p.actual.revenue),
          borderColor: 'rgba(34, 197, 94, 1)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          borderWidth: 2,
          fill: false
        }
      ]
    };
  };

  const getAchievementChartData = (executiveId) => {
    const data = getExecutivePerformanceData(executiveId);
    if (!data || !data.performance || !Array.isArray(data.performance)) return null;

    const labels = data.performance.map(p => {
      if (p.period === 'monthly' && p.month) return `${p.year}-${p.month.toString().padStart(2, '0')}`;
      if (p.period === 'quarterly' && p.quarter) return `Q${p.quarter} ${p.year}`;
      return `${p.year}`;
    });

    return {
      labels: labels,
      datasets: [
        {
          label: 'Revenue Achievement %',
          data: data.performance.map(p => p.achievement.revenue),
          borderColor: 'rgba(239, 68, 68, 1)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          borderWidth: 2,
          fill: false,
          yAxisID: 'y'
        },
        {
          label: 'Customer Achievement %',
          data: data.performance.map(p => p.achievement.customers),
          borderColor: 'rgba(251, 146, 60, 1)',
          backgroundColor: 'rgba(251, 146, 60, 0.1)',
          borderWidth: 2,
          fill: false,
          yAxisID: 'y'
        },
        {
          label: 'Order Achievement %',
          data: data.performance.map(p => p.achievement.orders),
          borderColor: 'rgba(168, 85, 247, 1)',
          backgroundColor: 'rgba(168, 85, 247, 0.1)',
          borderWidth: 2,
          fill: false,
          yAxisID: 'y'
        }
      ]
    };
  };

  const getIncentiveTrendChartData = (executiveId) => {
    const data = getExecutivePerformanceData(executiveId);
    if (!data || !data.performance || !Array.isArray(data.performance)) return null;

    const labels = data.performance.map(p => {
      if (p.period === 'monthly' && p.month) return `${p.year}-${p.month.toString().padStart(2, '0')}`;
      if (p.period === 'quarterly' && p.quarter) return `Q${p.quarter} ${p.year}`;
      return `${p.year}`;
    });

    return {
      labels: labels,
      datasets: [{
        label: 'Incentive Generated (₹)',
        data: data.performance.map(p => p.incentive),
        borderColor: 'rgba(34, 197, 94, 1)',
        backgroundColor: 'rgba(34, 197, 94, 0.2)',
        borderWidth: 3,
        fill: true,
        tension: 0.4
      }]
    };
  };

  // Salesman Dashboard Functions
  const getSalesmanPerformanceData = (executiveId) => {
    const executive = salesExecutives.find(se => se.id === executiveId);
    if (!executive) return null;

    // Get current month target
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();

    const currentTarget = targets.find(t =>
      t.salesExecutiveId === executiveId &&
      t.period === 'monthly' &&
      t.year === currentYear &&
      t.month === currentMonth
    );

    if (!currentTarget) return null;

    // Mock actual performance data (in real implementation, this would come from sales data)
    const actualPerformance = {
      revenue: currentTarget.revenueTarget * (0.7 + Math.random() * 0.6), // 70% to 130% of target
      customers: currentTarget.newCustomerTarget * (0.6 + Math.random() * 0.8), // 60% to 140% of target
      orders: currentTarget.orderTarget * (0.65 + Math.random() * 0.7) // 65% to 135% of target
    };

    // Calculate performance percentages
    const revenueAchievement = (actualPerformance.revenue / currentTarget.revenueTarget) * 100;
    const customerAchievement = (actualPerformance.customers / currentTarget.newCustomerTarget) * 100;
    const orderAchievement = (actualPerformance.orders / currentTarget.orderTarget) * 100;

    // Calculate incentive
    const incentive = calculateIncentive(executiveId, 'monthly', currentYear);

    // Calculate performance score (weighted average)
    const performanceScore = (revenueAchievement * 0.5) + (customerAchievement * 0.3) + (orderAchievement * 0.2);

    return {
      executive: executive,
      target: currentTarget,
      actual: actualPerformance,
      achievement: {
        revenue: revenueAchievement,
        customers: customerAchievement,
        orders: orderAchievement
      },
      incentive: incentive,
      performanceScore: performanceScore,
      tier: incentive?.tier?.name || 'Below Target'
    };
  };

  const getSalesmanTargetVsAchievedData = (executiveId) => {
    const data = getSalesmanPerformanceData(executiveId);
    if (!data) return null;

    return {
      labels: ['Revenue', 'Customers', 'Orders'],
      datasets: [
        {
          label: 'Target',
          data: [
            data.target.revenueTarget,
            data.target.newCustomerTarget,
            data.target.orderTarget
          ],
          backgroundColor: 'rgba(59, 130, 246, 0.8)',
          borderColor: 'rgba(59, 130, 246, 1)',
          borderWidth: 2
        },
        {
          label: 'Achieved',
          data: [
            data.actual.revenue,
            data.actual.customers,
            data.actual.orders
          ],
          backgroundColor: 'rgba(34, 197, 94, 0.8)',
          borderColor: 'rgba(34, 197, 94, 1)',
          borderWidth: 2
        }
      ]
    };
  };

  const getSalesmanPerformanceScoreData = (executiveId) => {
    const data = getSalesmanPerformanceData(executiveId);
    if (!data) return null;

    return {
      labels: ['Performance Score'],
      datasets: [{
        data: [data.performanceScore],
        backgroundColor: [
          data.performanceScore >= 120 ? 'rgba(34, 197, 94, 0.8)' :
            data.performanceScore >= 100 ? 'rgba(59, 130, 246, 0.8)' :
              data.performanceScore >= 80 ? 'rgba(251, 146, 60, 0.8)' :
                'rgba(239, 68, 68, 0.8)'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }]
    };
  };

  // Summary View Component
  const SummaryView = () => {
    const summary = calculateSummaryMetrics();

    return (
      <div className="space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Total Revenue Target</p>
                <p className="text-3xl font-bold">₹{summary.totalRevenueTarget.toLocaleString()}</p>
                <p className="text-blue-100 text-sm mt-1">
                  {summary.revenueProgress > 0 ? `${Math.round((summary.revenueProgress / summary.totalRevenueTarget) * 100)}% achieved` : 'No progress data'}
                </p>
              </div>
              <div className="text-blue-200">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z" />
                  <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">New Customers Target</p>
                <p className="text-3xl font-bold">{summary.totalCustomerTarget}</p>
                <p className="text-green-100 text-sm mt-1">
                  {summary.customerProgress > 0 ? `${Math.round((summary.customerProgress / summary.totalCustomerTarget) * 100)}% achieved` : 'No progress data'}
                </p>
              </div>
              <div className="text-green-200">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm font-medium">Order Target</p>
                <p className="text-3xl font-bold">{summary.totalOrderTarget}</p>
                <p className="text-orange-100 text-sm mt-1">
                  {summary.orderProgress > 0 ? `${Math.round((summary.orderProgress / summary.totalOrderTarget) * 100)}% achieved` : 'No progress data'}
                </p>
              </div>
              <div className="text-orange-200">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Time Progress</p>
                <p className="text-3xl font-bold">{summary.progressPercentage}%</p>
                <p className="text-purple-100 text-sm mt-1">
                  {summary.completedMonths} of {summary.totalMonths} months
                </p>
              </div>
              <div className="text-purple-200">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Revenue Trend</h3>
            <div className="h-64">
              <Line
                data={getMonthlyChartData()}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'top' },
                    title: { display: false }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        callback: function (value) {
                          return '₹' + value.toLocaleString();
                        }
                      }
                    }
                  }
                }}
              />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Progress Overview</h3>
            <div className="h-64">
              <Doughnut
                data={getProgressChartData()}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'bottom' }
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Monthly View Component
  const MonthlyView = () => {
    const monthlyData = generateMonthlyBreakdown();

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Breakdown - {selectedYear}</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue Target</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue Actual</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customers</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Orders</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {monthlyData.map((data, index) => {
                  const progress = data.revenueActual > 0 ? (data.revenueActual / data.revenueTarget) * 100 : 0;
                  return (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{data.month}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">₹{data.revenueTarget.toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">₹{data.revenueActual.toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: `${Math.min(progress, 100)}%` }}
                            ></div>
                          </div>
                          <span className="text-sm text-gray-900">{Math.round(progress)}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{data.customerTarget}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{data.orderTarget}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Individual View Component
  const IndividualView = () => {
    const executiveData = generateExecutiveComparison();

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Executive Performance - {selectedYear}</h3>
          <div className="h-80">
            <Bar
              data={getTeamExecutiveChartData()}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { position: 'top' }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      callback: function (value) {
                        return '₹' + value.toLocaleString();
                      }
                    }
                  }
                }
              }}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {executiveData.map((exec, index) => (
            <div key={index} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-900">{exec.name}</h4>
                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                  {Math.round((exec.revenueActual / exec.revenueTarget) * 100)}%
                </span>
              </div>

              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600">Revenue Target</p>
                  <p className="text-lg font-semibold text-gray-900">₹{exec.revenueTarget.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Revenue Actual</p>
                  <p className="text-lg font-semibold text-green-600">₹{exec.revenueActual.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Customers</p>
                  <p className="text-lg font-semibold text-gray-900">{exec.customerTarget} target / {exec.customerActual} actual</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Orders</p>
                  <p className="text-lg font-semibold text-gray-900">{exec.orderTarget} target / {exec.orderActual} actual</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Collective View Component
  const CollectiveView = () => {
    const summary = calculateSummaryMetrics();
    const monthlyData = generateMonthlyBreakdown();

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Performance</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Revenue</span>
                <span className="text-lg font-semibold text-gray-900">₹{summary.totalRevenueTarget.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Customers</span>
                <span className="text-lg font-semibold text-gray-900">{summary.totalCustomerTarget}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Orders</span>
                <span className="text-lg font-semibold text-gray-900">{summary.totalOrderTarget}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Seasonal Analysis</h3>
            <div className="space-y-3">
              {monthlyData.slice(0, 6).map((data, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">{data.month}</span>
                  <span className="text-sm font-medium text-gray-900">₹{data.revenueTarget.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Insights</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-sm text-gray-600">On Track</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <span className="text-sm text-gray-600">At Risk</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Behind Target</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quarterly Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(quarter => {
              const quarterMonths = monthlyData.slice((quarter - 1) * 3, quarter * 3);
              const quarterRevenue = quarterMonths.reduce((sum, month) => sum + month.revenueTarget, 0);
              return (
                <div key={quarter} className="text-center p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-gray-900">Q{quarter}</h4>
                  <p className="text-2xl font-bold text-blue-600">₹{quarterRevenue.toLocaleString()}</p>
                  <p className="text-sm text-gray-600">{quarterMonths.length} months</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Salesman Dashboard View Component
  const SalesmanDashboardView = () => {
    const [selectedExecutiveId, setSelectedExecutiveId] = useState(salesExecutives[0]?.id || '');
    const salesmanData = getSalesmanPerformanceData(selectedExecutiveId);

    if (!salesmanData) {
      return (
        <div className="text-center py-12">
          <p className="text-gray-500">No salesman data available</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Salesman Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Salesman Dashboard</h2>
              <p className="text-blue-100">Performance Overview & Incentive Tracking</p>
            </div>
            <select
              value={selectedExecutiveId}
              onChange={(e) => setSelectedExecutiveId(e.target.value)}
              className="bg-white text-gray-900 border-0 rounded-md px-3 py-2 text-sm font-medium"
            >
              {salesExecutives.map(exec => (
                <option key={exec.id} value={exec.id}>{exec.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Target vs Achieved Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Revenue Performance</h3>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${salesmanData.achievement.revenue >= 120 ? 'bg-green-100 text-green-800' :
                  salesmanData.achievement.revenue >= 100 ? 'bg-blue-100 text-blue-800' :
                    salesmanData.achievement.revenue >= 80 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                }`}>
                {salesmanData.achievement.revenue.toFixed(1)}%
              </span>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Target</span>
                <span className="text-lg font-semibold text-gray-900">₹{salesmanData.target.revenueTarget.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Achieved</span>
                <span className="text-lg font-semibold text-green-600">₹{salesmanData.actual.revenue.toLocaleString()}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${salesmanData.achievement.revenue >= 120 ? 'bg-green-500' :
                      salesmanData.achievement.revenue >= 100 ? 'bg-blue-500' :
                        salesmanData.achievement.revenue >= 80 ? 'bg-yellow-500' :
                          'bg-red-500'
                    }`}
                  style={{ width: `${Math.min(salesmanData.achievement.revenue, 150)}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Customer Performance</h3>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${salesmanData.achievement.customers >= 120 ? 'bg-green-100 text-green-800' :
                  salesmanData.achievement.customers >= 100 ? 'bg-blue-100 text-blue-800' :
                    salesmanData.achievement.customers >= 80 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                }`}>
                {salesmanData.achievement.customers.toFixed(1)}%
              </span>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Target</span>
                <span className="text-lg font-semibold text-gray-900">{salesmanData.target.newCustomerTarget}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Achieved</span>
                <span className="text-lg font-semibold text-green-600">{salesmanData.actual.customers}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${salesmanData.achievement.customers >= 120 ? 'bg-green-500' :
                      salesmanData.achievement.customers >= 100 ? 'bg-blue-500' :
                        salesmanData.achievement.customers >= 80 ? 'bg-yellow-500' :
                          'bg-red-500'
                    }`}
                  style={{ width: `${Math.min(salesmanData.achievement.customers, 150)}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Order Performance</h3>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${salesmanData.achievement.orders >= 120 ? 'bg-green-100 text-green-800' :
                  salesmanData.achievement.orders >= 100 ? 'bg-blue-100 text-blue-800' :
                    salesmanData.achievement.orders >= 80 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                }`}>
                {salesmanData.achievement.orders.toFixed(1)}%
              </span>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Target</span>
                <span className="text-lg font-semibold text-gray-900">{salesmanData.target.orderTarget}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Achieved</span>
                <span className="text-lg font-semibold text-green-600">{salesmanData.actual.orders}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${salesmanData.achievement.orders >= 120 ? 'bg-green-500' :
                      salesmanData.achievement.orders >= 100 ? 'bg-blue-500' :
                        salesmanData.achievement.orders >= 80 ? 'bg-yellow-500' :
                          'bg-red-500'
                    }`}
                  style={{ width: `${Math.min(salesmanData.achievement.orders, 150)}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Score & Incentive Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Score</h3>
            <div className="text-center">
              <div className="relative inline-block">
                <div className="w-32 h-32 rounded-full border-8 border-gray-200 flex items-center justify-center">
                  <div className="text-center">
                    <div className={`text-3xl font-bold ${salesmanData.performanceScore >= 120 ? 'text-green-600' :
                        salesmanData.performanceScore >= 100 ? 'text-blue-600' :
                          salesmanData.performanceScore >= 80 ? 'text-yellow-600' :
                            'text-red-600'
                      }`}>
                      {salesmanData.performanceScore.toFixed(1)}
                    </div>
                    <div className="text-sm text-gray-600">Score</div>
                  </div>
                </div>
                <div
                  className={`absolute inset-0 w-32 h-32 rounded-full border-8 border-transparent ${salesmanData.performanceScore >= 120 ? 'border-green-500' :
                      salesmanData.performanceScore >= 100 ? 'border-blue-500' :
                        salesmanData.performanceScore >= 80 ? 'border-yellow-500' :
                          'border-red-500'
                    }`}
                  style={{
                    clipPath: `polygon(50% 50%, 50% 0%, ${50 + (salesmanData.performanceScore / 2)}% 0%, ${50 + (salesmanData.performanceScore / 2)}% ${50 - (salesmanData.performanceScore / 2)}%, 50% 50%)`
                  }}
                ></div>
              </div>
              <div className="mt-4">
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${salesmanData.tier === 'Excellence' ? 'bg-purple-100 text-purple-800' :
                    salesmanData.tier === 'High Performance' ? 'bg-green-100 text-green-800' :
                      salesmanData.tier === 'Over Achievement' ? 'bg-orange-100 text-orange-800' :
                        salesmanData.tier === 'Target Achieved' ? 'bg-blue-100 text-blue-800' :
                          'bg-red-100 text-red-800'
                  }`}>
                  {salesmanData.tier}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Incentive Summary</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Base Commission</span>
                <span className="text-sm font-medium text-gray-900">₹{salesmanData.incentive?.breakdown?.baseCommission?.toLocaleString() || '0'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Performance Bonus</span>
                <span className="text-sm font-medium text-gray-900">₹{((salesmanData.incentive?.breakdown?.targetAchievementBonus || 0) + (salesmanData.incentive?.breakdown?.overAchievementBonus || 0)).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Volume Bonus</span>
                <span className="text-sm font-medium text-gray-900">₹{((salesmanData.incentive?.breakdown?.newCustomerBonus || 0) + (salesmanData.incentive?.breakdown?.orderVolumeBonus || 0)).toLocaleString()}</span>
              </div>
              <hr className="border-gray-200" />
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-gray-900">Total Incentive</span>
                <span className="text-2xl font-bold text-green-600">₹{salesmanData.incentive?.totalIncentive?.toLocaleString() || '0'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Target vs Achieved</h3>
            <div className="h-80">
              <Bar
                data={getSalesmanTargetVsAchievedData(selectedExecutiveId)}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'top' }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        callback: function (value) {
                          return '₹' + value.toLocaleString();
                        }
                      }
                    }
                  }
                }}
              />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Score</h3>
            <div className="h-80">
              <Doughnut
                data={getSalesmanPerformanceScoreData(selectedExecutiveId)}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { display: false }
                  }
                }}
              />
            </div>
          </div>
        </div>

        {/* Performance Breakdown Table */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Breakdown</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Metric</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Target</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Achieved</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Achievement %</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Revenue</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">₹{salesmanData.target.revenueTarget.toLocaleString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">₹{salesmanData.actual.revenue.toLocaleString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{salesmanData.achievement.revenue.toFixed(1)}%</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${salesmanData.achievement.revenue >= 120 ? 'bg-green-100 text-green-800' :
                        salesmanData.achievement.revenue >= 100 ? 'bg-blue-100 text-blue-800' :
                          salesmanData.achievement.revenue >= 80 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                      }`}>
                      {salesmanData.achievement.revenue >= 120 ? 'Excellent' :
                        salesmanData.achievement.revenue >= 100 ? 'Target Met' :
                          salesmanData.achievement.revenue >= 80 ? 'Near Target' :
                            'Below Target'}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">New Customers</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{salesmanData.target.newCustomerTarget}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{salesmanData.actual.customers}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{salesmanData.achievement.customers.toFixed(1)}%</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${salesmanData.achievement.customers >= 120 ? 'bg-green-100 text-green-800' :
                        salesmanData.achievement.customers >= 100 ? 'bg-blue-100 text-blue-800' :
                          salesmanData.achievement.customers >= 80 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                      }`}>
                      {salesmanData.achievement.customers >= 120 ? 'Excellent' :
                        salesmanData.achievement.customers >= 100 ? 'Target Met' :
                          salesmanData.achievement.customers >= 80 ? 'Near Target' :
                            'Below Target'}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Orders</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{salesmanData.target.orderTarget}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{salesmanData.actual.orders}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{salesmanData.achievement.orders.toFixed(1)}%</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${salesmanData.achievement.orders >= 120 ? 'bg-green-100 text-green-800' :
                        salesmanData.achievement.orders >= 100 ? 'bg-blue-100 text-blue-800' :
                          salesmanData.achievement.orders >= 80 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                      }`}>
                      {salesmanData.achievement.orders >= 120 ? 'Excellent' :
                        salesmanData.achievement.orders >= 100 ? 'Target Met' :
                          salesmanData.achievement.orders >= 80 ? 'Near Target' :
                            'Below Target'}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Executive Dashboard View Component
  const ExecutiveDashboardView = () => {
    const [selectedExecutiveId, setSelectedExecutiveId] = useState(salesExecutives[0]?.id || '');
    const executiveData = getExecutivePerformanceData(selectedExecutiveId);

    if (!executiveData) {
      return (
        <div className="text-center py-12">
          <p className="text-gray-500">No executive data available</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Executive Selector */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Sales Executive Dashboard</h3>
            <select
              value={selectedExecutiveId}
              onChange={(e) => setSelectedExecutiveId(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              {salesExecutives.map(exec => (
                <option key={exec.id} value={exec.id}>{exec.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Executive Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Total Revenue Achievement</p>
                <p className="text-3xl font-bold">
                  {((executiveData.summary.totalActualRevenue / executiveData.summary.totalTargetRevenue) * 100).toFixed(1)}%
                </p>
                <p className="text-blue-200 text-sm">
                  ₹{executiveData.summary.totalActualRevenue.toLocaleString()} / ₹{executiveData.summary.totalTargetRevenue.toLocaleString()}
                </p>
              </div>
              <div className="text-blue-200">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Total Incentive Generated</p>
                <p className="text-3xl font-bold">₹{executiveData.summary.totalIncentive.toLocaleString()}</p>
                <p className="text-green-200 text-sm">Performance-based rewards</p>
              </div>
              <div className="text-green-200">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm font-medium">Total Revenue Shortfall</p>
                <p className="text-3xl font-bold">₹{executiveData.summary.totalShortfall.revenue.toLocaleString()}</p>
                <p className="text-red-200 text-sm">Target vs Actual gap</p>
              </div>
              <div className="text-red-200">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Customer Achievement</p>
                <p className="text-3xl font-bold">
                  {((executiveData.summary.totalActualCustomers / executiveData.summary.totalTargetCustomers) * 100).toFixed(1)}%
                </p>
                <p className="text-purple-200 text-sm">
                  {executiveData.summary.totalActualCustomers} / {executiveData.summary.totalTargetCustomers} customers
                </p>
              </div>
              <div className="text-purple-200">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Target vs Actual Revenue</h3>
            <div className="h-80">
              <Line
                data={getIndividualExecutiveChartData(selectedExecutiveId)}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'top' }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        callback: function (value) {
                          return '₹' + value.toLocaleString();
                        }
                      }
                    }
                  }
                }}
              />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Achievement Percentage Trends</h3>
            <div className="h-80">
              <Line
                data={getAchievementChartData(selectedExecutiveId)}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'top' }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      max: 150,
                      ticks: {
                        callback: function (value) {
                          return value + '%';
                        }
                      }
                    }
                  }
                }}
              />
            </div>
          </div>
        </div>

        {/* Incentive Trend Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Incentive Generation Trend</h3>
          <div className="h-80">
            <Line
              data={getIncentiveTrendChartData(selectedExecutiveId)}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { position: 'top' }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      callback: function (value) {
                        return '₹' + value.toLocaleString();
                      }
                    }
                  }
                }
              }}
            />
          </div>
        </div>

        {/* Detailed Performance Table */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Detailed Performance Breakdown</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue Target</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue Actual</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Achievement %</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shortfall</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Incentive</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Performance Tier</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {executiveData.performance.map((perf, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {perf.period === 'monthly' ? `${perf.year}-${perf.month.toString().padStart(2, '0')}` :
                        perf.period === 'quarterly' ? `Q${perf.quarter} ${perf.year}` : `${perf.year}`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ₹{perf.target.revenue.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ₹{perf.actual.revenue.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${perf.achievement.revenue >= 120 ? 'bg-green-100 text-green-800' :
                          perf.achievement.revenue >= 100 ? 'bg-blue-100 text-blue-800' :
                            perf.achievement.revenue >= 80 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                        }`}>
                        {perf.achievement.revenue.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ₹{perf.shortfall.revenue.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                      ₹{perf.incentive.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${perf.tier === 'Excellence' ? 'bg-purple-100 text-purple-800' :
                          perf.tier === 'High Performance' ? 'bg-green-100 text-green-800' :
                            perf.tier === 'Over Achievement' ? 'bg-orange-100 text-orange-800' :
                              perf.tier === 'Target Achieved' ? 'bg-blue-100 text-blue-800' :
                                'bg-red-100 text-red-800'
                        }`}>
                        {perf.tier}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Shortfall Analysis */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Shortfall Analysis</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Shortfall</span>
                <span className="text-lg font-semibold text-red-600">₹{executiveData.summary.totalShortfall.revenue.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Average Monthly Gap</span>
                <span className="text-sm font-medium text-gray-900">
                  ₹{(executiveData.summary.totalShortfall.revenue / executiveData.performance.length).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Recovery Required</span>
                <span className="text-sm font-medium text-gray-900">
                  {((executiveData.summary.totalShortfall.revenue / executiveData.summary.totalTargetRevenue) * 100).toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Shortfall Analysis</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Shortfall</span>
                <span className="text-lg font-semibold text-red-600">{executiveData.summary.totalShortfall.customers}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Average Monthly Gap</span>
                <span className="text-sm font-medium text-gray-900">
                  {(executiveData.summary.totalShortfall.customers / executiveData.performance.length).toFixed(1)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Recovery Required</span>
                <span className="text-sm font-medium text-gray-900">
                  {((executiveData.summary.totalShortfall.customers / executiveData.summary.totalTargetCustomers) * 100).toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Shortfall Analysis</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Shortfall</span>
                <span className="text-lg font-semibold text-red-600">{executiveData.summary.totalShortfall.orders}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Average Monthly Gap</span>
                <span className="text-sm font-medium text-gray-900">
                  {(executiveData.summary.totalShortfall.orders / executiveData.performance.length).toFixed(1)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Recovery Required</span>
                <span className="text-sm font-medium text-gray-900">
                  {((executiveData.summary.totalShortfall.orders / executiveData.summary.totalTargetOrders) * 100).toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Incentive View Component
  const IncentiveView = () => {
    const teamIncentives = calculateTeamIncentives();

    return (
      <div className="space-y-6">
        {/* Incentive Configuration */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Incentive Configuration</h3>
            <button
              onClick={() => setShowIncentiveModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
            >
              Configure Incentives
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-600">Base Commission</p>
              <p className="text-lg font-semibold text-blue-600">{incentiveConfig.baseCommission}%</p>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <p className="text-sm text-gray-600">Target Bonus</p>
              <p className="text-lg font-semibold text-green-600">{incentiveConfig.targetAchievementBonus}%</p>
            </div>
            <div className="text-center p-3 bg-orange-50 rounded-lg">
              <p className="text-sm text-gray-600">Over Achievement</p>
              <p className="text-lg font-semibold text-orange-600">{incentiveConfig.overAchievementBonus}%</p>
            </div>
            <div className="text-center p-3 bg-purple-50 rounded-lg">
              <p className="text-sm text-gray-600">New Customer Bonus</p>
              <p className="text-lg font-semibold text-purple-600">₹{incentiveConfig.newCustomerBonus}</p>
            </div>
          </div>
        </div>

        {/* Team Incentive Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Total Team Incentive</p>
                <p className="text-3xl font-bold">₹{teamIncentives.summary.totalIncentive.toLocaleString()}</p>
              </div>
              <div className="text-green-200">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Average Incentive</p>
                <p className="text-3xl font-bold">₹{teamIncentives.summary.averageIncentive.toLocaleString()}</p>
              </div>
              <div className="text-blue-200">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Highest Incentive</p>
                <p className="text-3xl font-bold">₹{teamIncentives.summary.highestIncentive.toLocaleString()}</p>
              </div>
              <div className="text-purple-200">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm font-medium">Lowest Incentive</p>
                <p className="text-3xl font-bold">₹{teamIncentives.summary.lowestIncentive.toLocaleString()}</p>
              </div>
              <div className="text-orange-200">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Individual Incentives</h3>
            <div className="h-80">
              <Bar
                data={getIncentiveChartData()}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { display: false }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        callback: function (value) {
                          return '₹' + value.toLocaleString();
                        }
                      }
                    }
                  }
                }}
              />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Tiers Distribution</h3>
            <div className="h-80">
              <Doughnut
                data={getPerformanceTierChartData()}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'bottom' }
                  }
                }}
              />
            </div>
          </div>
        </div>

        {/* Individual Incentive Details */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Detailed Incentive Breakdown</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Executive</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Performance Tier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue Achievement</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Base Commission</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Performance Bonus</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Incentive</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {teamIncentives.individual.map((incentive, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {incentive.executive}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${incentive.tier.name === 'Excellence' ? 'bg-purple-100 text-purple-800' :
                          incentive.tier.name === 'High Performance' ? 'bg-green-100 text-green-800' :
                            incentive.tier.name === 'Over Achievement' ? 'bg-orange-100 text-orange-800' :
                              incentive.tier.name === 'Target Achieved' ? 'bg-blue-100 text-blue-800' :
                                'bg-red-100 text-red-800'
                        }`}>
                        {incentive.tier.name}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {incentive.achievement.revenue.toFixed(1)}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ₹{incentive.breakdown.baseCommission.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ₹{(incentive.breakdown.targetAchievementBonus + incentive.breakdown.overAchievementBonus + incentive.breakdown.newCustomerBonus + incentive.breakdown.orderVolumeBonus).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                      ₹{incentive.totalIncentive.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const handleCreateTarget = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiService.post('/sales-targets', formData);
      setShowCreateModal(false);
      setFormData({
        salesExecutiveId: '',
        period: 'monthly',
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        quarter: Math.ceil((new Date().getMonth() + 1) / 3),
        startDate: '',
        endDate: '',
        newCustomerTarget: 0,
        revenueTarget: 0,
        orderTarget: 0,
        notes: ''
      });
      fetchTargets();
    } catch (err) {
      setError('Failed to create sales target');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTarget = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiService.put(`/sales-targets/${editingTarget.id}`, formData);
      setEditingTarget(null);
      setFormData({
        salesExecutiveId: '',
        period: 'monthly',
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        quarter: Math.ceil((new Date().getMonth() + 1) / 3),
        startDate: '',
        endDate: '',
        newCustomerTarget: 0,
        revenueTarget: 0,
        orderTarget: 0,
        notes: ''
      });
      fetchTargets();
    } catch (err) {
      setError('Failed to update sales target');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTarget = async (id) => {
    if (!window.confirm('Are you sure you want to delete this target?')) return;

    setLoading(true);
    try {
      await apiService.delete(`/sales-targets/${id}`);
      fetchTargets();
    } catch (err) {
      setError('Failed to delete sales target');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (target) => {
    setEditingTarget(target);

    // Calculate year, month, quarter from the target dates
    const startDate = new Date(target.startDate);
    const year = startDate.getFullYear();
    const month = startDate.getMonth() + 1;
    const quarter = Math.ceil(month / 3);

    const formDataWithDates = {
      salesExecutiveId: target.salesExecutiveId.toString(),
      period: target.period,
      year: year,
      month: month,
      quarter: quarter,
      startDate: target.startDate.split('T')[0],
      endDate: target.endDate.split('T')[0],
      newCustomerTarget: target.newCustomerTarget,
      revenueTarget: target.revenueTarget,
      orderTarget: target.orderTarget,
      notes: target.notes || ''
    };

    setFormData(updateDateRange(formDataWithDates));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  const getProgressPercentage = (actual, target) => {
    if (target === 0) return 0;
    return Math.min((actual / target) * 100, 100);
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 100) return 'bg-green-500';
    if (percentage >= 75) return 'bg-blue-500';
    if (percentage >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 py-6 px-4 sm:px-6 lg:px-8">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Sales Targets Management</h1>
          <p className="mt-2 text-gray-600">Set and monitor sales targets for executives</p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowDashboard(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <span>Intelligence Dashboard</span>
          </button>
          <div className="text-sm text-gray-500">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </div>
          <button
            onClick={fetchTargets}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>{loading ? 'Refreshing...' : 'Refresh Data'}</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sales Executive</label>
            <select
              value={filters.salesExecutiveId}
              onChange={(e) => setFilters({ ...filters, salesExecutiveId: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All Executives</option>
              {salesExecutives.map(exec => (
                <option key={exec.id} value={exec.id}>{exec.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
            <select
              value={filters.period}
              onChange={(e) => setFilters({ ...filters, period: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All Periods</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setShowCreateModal(true)}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Create New Target
            </button>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {/* Targets Table */}
      {!loading && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Executive</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">New Customers</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Orders</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {targets.map((target) => (
                  <tr key={target.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{target.salesExecutive.name}</div>
                        <div className="text-sm text-gray-500">{target.salesExecutive.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900 capitalize">{target.period}</div>
                        <div className="text-sm text-gray-500">
                          {formatDate(target.startDate)} - {formatDate(target.endDate)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {target.actualNewCustomers} / {target.newCustomerTarget}
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                          <div
                            className={`h-2 rounded-full ${getProgressColor(getProgressPercentage(target.actualNewCustomers, target.newCustomerTarget))}`}
                            style={{ width: `${getProgressPercentage(target.actualNewCustomers, target.newCustomerTarget)}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(target.actualRevenue)} / {formatCurrency(target.revenueTarget)}
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                          <div
                            className={`h-2 rounded-full ${getProgressColor(getProgressPercentage(target.actualRevenue, target.revenueTarget))}`}
                            style={{ width: `${getProgressPercentage(target.actualRevenue, target.revenueTarget)}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {target.actualOrders} / {target.orderTarget}
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                          <div
                            className={`h-2 rounded-full ${getProgressColor(getProgressPercentage(target.actualOrders, target.orderTarget))}`}
                            style={{ width: `${getProgressPercentage(target.actualOrders, target.orderTarget)}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${target.status === 'completed' ? 'bg-green-100 text-green-800' :
                          target.status === 'active' ? 'bg-blue-100 text-blue-800' :
                            'bg-red-100 text-red-800'
                        }`}>
                        {target.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => openEditModal(target)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteTarget(target.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingTarget) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingTarget ? 'Edit Sales Target' : 'Create New Sales Target'}
              </h3>
              <form onSubmit={editingTarget ? handleUpdateTarget : handleCreateTarget}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sales Executive</label>
                    <select
                      value={formData.salesExecutiveId}
                      onChange={(e) => setFormData({ ...formData, salesExecutiveId: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      required
                    >
                      <option value="">Select Executive</option>
                      {salesExecutives.map(exec => (
                        <option key={exec.id} value={exec.id}>{exec.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
                    <select
                      value={formData.period}
                      onChange={(e) => {
                        const newFormData = { ...formData, period: e.target.value };
                        setFormData(updateDateRange(newFormData));
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      required
                    >
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                    <select
                      value={formData.year}
                      onChange={(e) => {
                        const newFormData = { ...formData, year: parseInt(e.target.value) };
                        setFormData(updateDateRange(newFormData));
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      required
                    >
                      {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() + i).map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>

                  {formData.period === 'monthly' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                      <select
                        value={formData.month}
                        onChange={(e) => {
                          const newFormData = { ...formData, month: parseInt(e.target.value) };
                          setFormData(updateDateRange(newFormData));
                        }}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        required
                      >
                        <option value={1}>January</option>
                        <option value={2}>February</option>
                        <option value={3}>March</option>
                        <option value={4}>April</option>
                        <option value={5}>May</option>
                        <option value={6}>June</option>
                        <option value={7}>July</option>
                        <option value={8}>August</option>
                        <option value={9}>September</option>
                        <option value={10}>October</option>
                        <option value={11}>November</option>
                        <option value={12}>December</option>
                      </select>
                    </div>
                  )}

                  {formData.period === 'quarterly' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Quarter</label>
                      <select
                        value={formData.quarter}
                        onChange={(e) => {
                          const newFormData = { ...formData, quarter: parseInt(e.target.value) };
                          setFormData(updateDateRange(newFormData));
                        }}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        required
                      >
                        <option value={1}>Q1 (Jan-Mar)</option>
                        <option value={2}>Q2 (Apr-Jun)</option>
                        <option value={3}>Q3 (Jul-Sep)</option>
                        <option value={4}>Q4 (Oct-Dec)</option>
                      </select>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Start Date (Auto-calculated)</label>
                      <input
                        type="date"
                        value={formData.startDate}
                        readOnly
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-gray-50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">End Date (Auto-calculated)</label>
                      <input
                        type="date"
                        value={formData.endDate}
                        readOnly
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-gray-50"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">New Customers</label>
                      <input
                        type="number"
                        value={formData.newCustomerTarget}
                        onChange={(e) => setFormData({ ...formData, newCustomerTarget: parseInt(e.target.value) || 0 })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Revenue Target</label>
                      <input
                        type="number"
                        value={formData.revenueTarget}
                        onChange={(e) => setFormData({ ...formData, revenueTarget: parseFloat(e.target.value) || 0 })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Order Target</label>
                      <input
                        type="number"
                        value={formData.orderTarget}
                        onChange={(e) => setFormData({ ...formData, orderTarget: parseInt(e.target.value) || 0 })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        min="0"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      rows="3"
                    />
                  </div>

                  {/* Intelligent Allocation Info for Annual Targets */}
                  {formData.period === 'yearly' && !editingTarget && (
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-blue-800">Intelligent Monthly Allocation</h3>
                          <div className="mt-2 text-sm text-blue-700">
                            <p>When you create an annual target, the system will automatically create 12 monthly targets with intelligent allocation based on business seasonality:</p>
                            <ul className="mt-2 list-disc list-inside space-y-1">
                              <li><strong>Peak Months (Jun, Oct):</strong> 10% of annual target</li>
                              <li><strong>High Activity (Mar, May, Sep, Nov):</strong> 9% of annual target</li>
                              <li><strong>Moderate (Feb, Apr, Jul, Aug):</strong> 8% of annual target</li>
                              <li><strong>Recovery (Jan):</strong> 7% of annual target</li>
                              <li><strong>Year-end (Dec):</strong> 5% of annual target</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setEditingTarget(null);
                      setFormData({
                        salesExecutiveId: '',
                        period: 'monthly',
                        year: new Date().getFullYear(),
                        month: new Date().getMonth() + 1,
                        quarter: Math.ceil((new Date().getMonth() + 1) / 3),
                        startDate: '',
                        endDate: '',
                        newCustomerTarget: 0,
                        revenueTarget: 0,
                        orderTarget: 0,
                        notes: ''
                      });
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>

                  {/* Show different buttons based on period and mode */}
                  {editingTarget ? (
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                      disabled={loading}
                    >
                      {loading ? 'Updating...' : 'Update'}
                    </button>
                  ) : formData.period === 'yearly' ? (
                    <button
                      type="button"
                      onClick={handleCreateAnnualTarget}
                      className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                      disabled={loading}
                    >
                      {loading ? 'Creating Annual + Monthly...' : 'Create Annual + Auto-allocate Monthly'}
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                      disabled={loading}
                    >
                      {loading ? 'Saving...' : 'Create'}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Sales Target Dashboard Modal */}
      {showDashboard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-7xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-4">
                <h2 className="text-2xl font-bold text-gray-900">Sales Target Intelligence Dashboard</h2>
                <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                  {selectedYear}
                </span>
              </div>
              <button
                onClick={() => setShowDashboard(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Dashboard Controls */}
            <div className="p-6 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {/* View Mode Selector */}
                  <div className="flex bg-white rounded-lg shadow-sm border">
                    {['summary', 'monthly', 'individual', 'collective', 'incentive', 'executive', 'salesman'].map((mode) => (
                      <button
                        key={mode}
                        onClick={() => setViewMode(mode)}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${viewMode === mode
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          }`}
                      >
                        {mode.charAt(0).toUpperCase() + mode.slice(1)}
                      </button>
                    ))}
                  </div>

                  {/* Year Selector */}
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                  >
                    {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() + i).map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>

                  {/* Executive Filter */}
                  <select
                    value={selectedExecutive}
                    onChange={(e) => setSelectedExecutive(e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                  >
                    <option value="">All Executives</option>
                    {salesExecutives.map(exec => (
                      <option key={exec.id} value={exec.id.toString()}>
                        {exec.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <span>Last updated: {lastRefresh.toLocaleTimeString()}</span>
                  <button
                    onClick={() => fetchTargets()}
                    className="p-2 text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Dashboard Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              {viewMode === 'summary' && <SummaryView />}
              {viewMode === 'monthly' && <MonthlyView />}
              {viewMode === 'individual' && <IndividualView />}
              {viewMode === 'collective' && <CollectiveView />}
              {viewMode === 'incentive' && <IncentiveView />}
              {viewMode === 'executive' && <ExecutiveDashboardView />}
              {viewMode === 'salesman' && <SalesmanDashboardView />}
            </div>
          </div>
        </div>
      )}

      {/* Incentive Configuration Modal */}
      {showIncentiveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Incentive Configuration</h2>
              <button
                onClick={() => setShowIncentiveModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              <div className="space-y-6">
                {/* Commission Structure */}
                <div className="bg-white rounded-lg border p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Commission Structure</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Base Commission (%)</label>
                      <input
                        type="number"
                        value={incentiveConfig.baseCommission}
                        onChange={(e) => setIncentiveConfig({
                          ...incentiveConfig,
                          baseCommission: parseFloat(e.target.value) || 0
                        })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        step="0.1"
                        min="0"
                        max="10"
                      />
                      <p className="text-xs text-gray-500 mt-1">Standard commission on revenue</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Target Achievement Bonus (%)</label>
                      <input
                        type="number"
                        value={incentiveConfig.targetAchievementBonus}
                        onChange={(e) => setIncentiveConfig({
                          ...incentiveConfig,
                          targetAchievementBonus: parseFloat(e.target.value) || 0
                        })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        step="0.1"
                        min="0"
                        max="10"
                      />
                      <p className="text-xs text-gray-500 mt-1">Bonus for achieving 100% target</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Over Achievement Bonus (%)</label>
                      <input
                        type="number"
                        value={incentiveConfig.overAchievementBonus}
                        onChange={(e) => setIncentiveConfig({
                          ...incentiveConfig,
                          overAchievementBonus: parseFloat(e.target.value) || 0
                        })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        step="0.1"
                        min="0"
                        max="15"
                      />
                      <p className="text-xs text-gray-500 mt-1">Bonus for exceeding 120% target</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">New Customer Bonus (₹)</label>
                      <input
                        type="number"
                        value={incentiveConfig.newCustomerBonus}
                        onChange={(e) => setIncentiveConfig({
                          ...incentiveConfig,
                          newCustomerBonus: parseInt(e.target.value) || 0
                        })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        min="0"
                        step="100"
                      />
                      <p className="text-xs text-gray-500 mt-1">Fixed bonus per new customer</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Order Volume Bonus (₹)</label>
                      <input
                        type="number"
                        value={incentiveConfig.orderVolumeBonus}
                        onChange={(e) => setIncentiveConfig({
                          ...incentiveConfig,
                          orderVolumeBonus: parseInt(e.target.value) || 0
                        })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        min="0"
                        step="50"
                      />
                      <p className="text-xs text-gray-500 mt-1">Fixed bonus per order</p>
                    </div>
                  </div>
                </div>

                {/* Performance Tiers */}
                <div className="bg-white rounded-lg border p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Tiers</h3>
                  <div className="space-y-4">
                    {incentiveConfig.performanceTiers.map((tier, index) => (
                      <div key={index} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">{tier.name}</label>
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              value={tier.min}
                              onChange={(e) => {
                                const newTiers = [...incentiveConfig.performanceTiers];
                                newTiers[index].min = parseInt(e.target.value) || 0;
                                setIncentiveConfig({
                                  ...incentiveConfig,
                                  performanceTiers: newTiers
                                });
                              }}
                              className="w-20 border border-gray-300 rounded-md px-2 py-1 text-sm"
                              min="0"
                              max="200"
                            />
                            <span className="text-sm text-gray-500">to</span>
                            <input
                              type="number"
                              value={tier.max}
                              onChange={(e) => {
                                const newTiers = [...incentiveConfig.performanceTiers];
                                newTiers[index].max = parseInt(e.target.value) || 0;
                                setIncentiveConfig({
                                  ...incentiveConfig,
                                  performanceTiers: newTiers
                                });
                              }}
                              className="w-20 border border-gray-300 rounded-md px-2 py-1 text-sm"
                              min="0"
                              max="200"
                            />
                            <span className="text-sm text-gray-500">%</span>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Multiplier</label>
                          <input
                            type="number"
                            value={tier.multiplier}
                            onChange={(e) => {
                              const newTiers = [...incentiveConfig.performanceTiers];
                              newTiers[index].multiplier = parseFloat(e.target.value) || 0;
                              setIncentiveConfig({
                                ...incentiveConfig,
                                performanceTiers: newTiers
                              });
                            }}
                            className="w-20 border border-gray-300 rounded-md px-2 py-1 text-sm"
                            step="0.1"
                            min="0"
                            max="5"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Industry Standards Reference */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-blue-900 mb-4">Industry Standards Reference</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <h4 className="font-medium text-blue-800 mb-2">Commission Ranges:</h4>
                      <ul className="space-y-1 text-blue-700">
                        <li>• Base Commission: 1.5% - 3.5%</li>
                        <li>• Target Bonus: 3% - 7%</li>
                        <li>• Over Achievement: 5% - 12%</li>
                        <li>• New Customer Bonus: ₹300 - ₹1000</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium text-blue-800 mb-2">Performance Multipliers:</h4>
                      <ul className="space-y-1 text-blue-700">
                        <li>• Below Target (0-80%): 0.3x - 0.7x</li>
                        <li>• Target Achieved (80-100%): 1.0x</li>
                        <li>• Over Achievement (100-120%): 1.2x - 1.4x</li>
                        <li>• High Performance (120-150%): 1.5x - 1.8x</li>
                        <li>• Excellence (150%+): 2.0x - 2.5x</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setShowIncentiveModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowIncentiveModal(false);
                  toast.success('Incentive configuration updated successfully!');
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Save Configuration
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesTargets;
